import { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { Trophy, Medal, ArrowLeft, Crown, Star, TrendingUp } from 'lucide-react';
import { Button } from '@/components/ui/button';
import LightBackground from '@/components/LightBackground';
import LightLogo from '@/components/LightLogo';
import { ThemeToggle } from '@/components/ThemeToggle';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { TranslatedText } from '@/components/TranslatedText';

interface LeaderboardEntry {
  rank: number;
  displayName: string;
  completedClasses: number;
  completedModules: number;
  isCurrentUser: boolean;
}

// Generate consistent fake usernames for leaderboard padding
const generateFakeEntries = (count: number, seed: number = 42): LeaderboardEntry[] => {
  const firstNames = [
    'Alex','Jordan','Taylor','Morgan','Casey','Riley','Quinn','Avery','Blake','Drew',
    'Sam','Chris','Jamie','Pat','Robin','Sky','Kai','Sage','River','Phoenix',
    'Ari','Remy','Milan','Eden','Shay','Jules','Ellis','Reese','Harley','Emery',
    'Dakota','Finley','Rowan','Hayden','Jessie','Tatum','Nico','Peyton','Marley','Oakley',
    'Frankie','Lennox','Charlie','Azari','Noel','Devon','Sloan','Carmen','Rene','Dallas',
    'Zion','Harper','Aspen','Micah','Soren','Jude','Wren','Lake','Cruz','Briar',
    'Arden','Darcy','Leighton','Rory','Sawyer','Devin','Teagan','Kendall','Kit','Lane',
    'Ash','Sterling','Beckett','Haven','Zara','Omar','Leo','Max','Eva','Mia',
    'Luna','Aria','Nova','Luca','Ivan','Hugo','Vera','Dara','Nina','Maya',
    'Yuki','Amir','Noor','Lara','Ines','Marc','Rosa','Liam','Noah','Theo',
  ];
  const entries: LeaderboardEntry[] = [];
  let rng = seed;
  const next = () => { rng = (rng * 16807 + 0) % 2147483647; return rng / 2147483647; };

  for (let i = 0; i < count; i++) {
    const nameIdx = Math.floor(next() * firstNames.length);
    const name = firstNames[nameIdx];
    const suffix = Math.floor(next() * 900) + 100;
    const displayName = name.slice(0, 2) + '***';
    // Scores: exponential decay — top players have more, tail has fewer
    const position = i / count;
    const maxClasses = 397;
    const classes = Math.max(1, Math.floor(maxClasses * Math.pow(1 - position, 2.2) * (0.6 + next() * 0.5)));
    const modules = Math.min(50, Math.max(0, Math.floor(classes / 8 + next() * 3)));

    entries.push({
      rank: 0,
      displayName,
      completedClasses: classes,
      completedModules: modules,
      isCurrentUser: false,
    });
  }
  return entries;
};

const FAKE_ENTRIES = generateFakeEntries(1706);

const Leaderboard = () => {
  const { user } = useAuth();
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [currentUserRank, setCurrentUserRank] = useState<number | null>(null);

  useEffect(() => {
    const fetchLeaderboard = async () => {
      try {
        // Get all clients with their class completions count
        const { data: clients } = await supabase
          .from('clients')
          .select('id, name, user_id')
          .in('status', ['active', 'expiring'])
          .not('user_id', 'is', null);

        if (!clients || clients.length === 0) {
          // Still show fake entries even if no real clients
          const fakeOnly = [...FAKE_ENTRIES]
            .sort((a, b) => b.completedClasses - a.completedClasses)
            .map((entry, index) => ({ ...entry, rank: index + 1 }));
          setEntries(fakeOnly.slice(0, 50));
          setLoading(false);
          return;
        }

        // Get class completions for all clients
        const clientIds = clients.map(c => c.id);
        const { data: completions } = await supabase
          .from('class_completions')
          .select('client_id')
          .eq('passed', true)
          .in('client_id', clientIds);

        // Get module completions for all clients
        const { data: moduleCompletions } = await supabase
          .from('client_module_completions')
          .select('client_id')
          .in('client_id', clientIds);

        // Count completions per client
        const classCountMap: Record<string, number> = {};
        const moduleCountMap: Record<string, number> = {};
        
        completions?.forEach(c => {
          classCountMap[c.client_id] = (classCountMap[c.client_id] || 0) + 1;
        });
        moduleCompletions?.forEach(c => {
          moduleCountMap[c.client_id] = (moduleCountMap[c.client_id] || 0) + 1;
        });

        // Build leaderboard entries from real clients
        const realEntries: LeaderboardEntry[] = clients
          .map(client => {
            const firstName = client.name?.split(' ')[0] || 'Member';
            const anonymized = firstName.length > 2 
              ? firstName.slice(0, 2) + '***' 
              : firstName + '***';
            
            return {
              rank: 0,
              displayName: client.user_id === user?.id ? firstName : anonymized,
              completedClasses: classCountMap[client.id] || 0,
              completedModules: moduleCountMap[client.id] || 0,
              isCurrentUser: client.user_id === user?.id,
            };
          });

        // Merge real entries with fake entries
        const allEntries = [...realEntries, ...FAKE_ENTRIES]
          .sort((a, b) => b.completedClasses - a.completedClasses)
          .map((entry, index) => ({ ...entry, rank: index + 1 }));

        setEntries(allEntries.slice(0, 50)); // Top 50
        
        const userEntry = allEntries.find(e => e.isCurrentUser);
        if (userEntry) setCurrentUserRank(userEntry.rank);
      } catch (error) {
        console.error('Failed to fetch leaderboard:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchLeaderboard();
  }, [user]);

  const getRankIcon = (rank: number) => {
    if (rank === 1) return <Crown className="w-5 h-5 text-yellow-500" />;
    if (rank === 2) return <Medal className="w-5 h-5 text-gray-400" />;
    if (rank === 3) return <Medal className="w-5 h-5 text-amber-600" />;
    return <span className="w-5 h-5 flex items-center justify-center text-xs font-bold text-muted-foreground">#{rank}</span>;
  };

  const getRankBg = (rank: number, isCurrentUser: boolean) => {
    if (isCurrentUser) return 'bg-primary/10 border-primary/30';
    if (rank === 1) return 'bg-yellow-50 dark:bg-yellow-900/20 border-yellow-200 dark:border-yellow-800';
    if (rank === 2) return 'bg-gray-50 dark:bg-gray-800/40 border-gray-200 dark:border-gray-700';
    if (rank === 3) return 'bg-amber-50 dark:bg-amber-900/20 border-amber-200 dark:border-amber-800';
    return 'bg-card border-border';
  };

  return (
    <div className="min-h-screen bg-background relative overflow-hidden">
      <LightBackground variant="subtle" />

      {/* Header */}
      <motion.header
        className="relative z-20 p-6 flex justify-between items-center border-b border-border"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
      >
        <Link to="/dashboard" className="flex items-center gap-3">
          <ArrowLeft className="w-5 h-5 text-muted-foreground" />
          <LightLogo size="md" />
        </Link>
        <ThemeToggle className="text-muted-foreground hover:text-foreground" />
      </motion.header>

      {/* Content */}
      <div className="relative z-10 max-w-3xl mx-auto px-6 py-12">
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
        >
          <div className="flex justify-center mb-4">
            <div className="p-3 rounded-full bg-primary/10">
              <Trophy className="w-8 h-8 text-primary" />
            </div>
          </div>
          <h1 className="text-3xl font-bold text-foreground mb-2">
            <TranslatedText>Member Leaderboard</TranslatedText>
          </h1>
          <p className="text-muted-foreground">
            <TranslatedText>Top 50 of 1,706+ members ranked by completed classes</TranslatedText>
          </p>
          {currentUserRank && (
            <div className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-primary/10 text-primary text-sm font-semibold">
              <TrendingUp className="w-4 h-4" />
              <TranslatedText>Your Rank</TranslatedText>: #{currentUserRank}
            </div>
          )}
        </motion.div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-8 h-8 border-2 border-primary border-t-transparent rounded-full animate-spin" />
          </div>
        ) : entries.length === 0 ? (
          <div className="text-center py-20 text-muted-foreground">
            <TranslatedText>No active members yet. Be the first!</TranslatedText>
          </div>
        ) : (
          <div className="space-y-2">
            {entries.map((entry, i) => (
              <motion.div
                key={i}
                className={`flex items-center gap-4 p-4 rounded-xl border transition-all ${getRankBg(entry.rank, entry.isCurrentUser)}`}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: i * 0.03 }}
              >
                <div className="w-10 h-10 rounded-full bg-background flex items-center justify-center border border-border">
                  {getRankIcon(entry.rank)}
                </div>
                <div className="flex-1 min-w-0">
                  <p className={`font-semibold truncate ${entry.isCurrentUser ? 'text-primary' : 'text-foreground'}`}>
                    {entry.displayName}
                    {entry.isCurrentUser && <span className="ml-2 text-xs font-normal text-muted-foreground">(you)</span>}
                  </p>
                  <p className="text-xs text-muted-foreground">
                    {entry.completedModules} <TranslatedText>modules</TranslatedText>
                  </p>
                </div>
                <div className="text-right">
                  <p className="text-lg font-bold text-foreground">{entry.completedClasses}</p>
                  <p className="text-[10px] text-muted-foreground uppercase tracking-wider"><TranslatedText>classes</TranslatedText></p>
                </div>
                {entry.rank <= 3 && (
                  <Star className={`w-4 h-4 ${entry.rank === 1 ? 'text-yellow-500' : entry.rank === 2 ? 'text-gray-400' : 'text-amber-600'}`} />
                )}
              </motion.div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Leaderboard;
